import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const MultiSelect = ({ options, labelKey, idKey, selected, onChange, testid }) => {
  const [q, setQ] = useState("");
  const filtered = options.filter((o) => `${o[labelKey]} ${o.code || ""}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid={testid}>
      <input className={inputCls} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
      <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y">
        {filtered.map((o) => (
          <label key={o[idKey]} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-opt-${o[idKey]}`}>
            <input type="checkbox" checked={selected.includes(o[idKey])} onChange={() => toggle(o[idKey])} />
            <span className="text-slate-800">{o[labelKey]}</span>
            {o.price != null && <span className="ml-auto text-xs text-slate-500">₹{Number(o.price).toLocaleString("en-IN")}</span>}
          </label>
        ))}
        {!filtered.length && <p className="px-3 py-4 text-xs text-slate-400">Nothing found.</p>}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">{selected.length} selected</p>
    </div>
  );
};

export default function LabPackages() {
  const { can } = useAuth();
  const manage = can("lab.tests.manage");
  const [items, setItems] = useState(null);
  const [tests, setTests] = useState([]);
  const [panels, setPanels] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", fee: "", gender_applicability: "all", test_ids: [], panel_ids: [], active: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [{ data: pk }, { data: t }, { data: pn }] = await Promise.all([
        api.get("/lab/packages"), api.get("/lab/tests", { params: { active: true } }), api.get("/lab/panels", { params: { active: true } })]);
      setItems(pk.items); setTests(t.items); setPanels(pn.items);
    } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", fee: "", gender_applicability: "all", test_ids: [], panel_ids: [], active: true }); setFormError(""); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, fee: String(p.fee ?? ""), gender_applicability: p.gender_applicability || "all", test_ids: p.test_ids || [], panel_ids: p.panel_ids || [], active: p.active }); setFormError(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Package name is required."); return; }
    if (!(Number(form.fee) > 0)) { setFormError("Package fee must be greater than zero."); return; }
    if (!form.test_ids.length && !form.panel_ids.length) { setFormError("Add at least one test or panel."); return; }
    setBusy(true); setFormError("");
    try {
      const payload = { ...form, fee: Number(form.fee) };
      if (editing) await api.patch(`/lab/packages/${editing.package_id}`, payload);
      else await api.post("/lab/packages", payload);
      toast.success(editing ? "Package updated." : "Package added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };
  const toggle = async (p) => { try { await api.patch(`/lab/packages/${p.package_id}`, { active: !p.active }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!can("lab.tests.view")) return <NoAccess what="Lab test packages" />;

  return (
    <div data-testid="lab-packages-page">
      <PageHeader overline="Laboratory" title="Test Packages"
        subtitle="Bundle tests and panels into a discounted health-checkup package with a single fee."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-package-btn"><Plus className="h-4 w-4" /> Add package</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-packages-table" columns={["#", "Package", "Fee (₹)", "Gender", "Includes", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No packages yet" subtitle="Create your first checkup package." testid="lab-packages-empty" />}
          renderRow={(p, i) => (
            <tr key={p.package_id} data-testid={`lab-package-row-${p.package_id}`}>
              <Td className="text-slate-400">{i + 1}</Td>
              <Td className="font-medium text-slate-900">{p.name}</Td>
              <Td className="text-slate-700">{Number(p.fee).toLocaleString("en-IN")}</Td>
              <Td className="text-slate-600 capitalize">{p.gender_applicability}</Td>
              <Td className="text-slate-600">{(p.tests || []).length} tests · {(p.panels || []).length} panels</Td>
              <Td><span className={`text-xs font-medium ${p.active ? "text-emerald-600" : "text-slate-400"}`}>{p.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className={ghostBtnCls} data-testid={`edit-package-${p.package_id}`}>Edit</button>
                  <button onClick={() => toggle(p)} className={ghostBtnCls} data-testid={`toggle-package-${p.package_id}`}>{p.active ? "Archive" : "Restore"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit package" : "Add package"} wide testid="lab-package-modal">
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Package name *"><input className={inputCls} data-testid="package-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Fee (₹) *"><input type="number" min="1" className={inputCls} data-testid="package-fee" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} /></Field>
            <Field label="Gender applicability">
              <select className={inputCls} data-testid="package-gender" value={form.gender_applicability} onChange={(e) => setForm({ ...form, gender_applicability: e.target.value })}>
                <option value="all">All</option><option value="male">Male only</option><option value="female">Female only</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tests"><MultiSelect options={tests} labelKey="name" idKey="test_id" selected={form.test_ids} onChange={(ids) => setForm({ ...form, test_ids: ids })} testid="package-tests" /></Field>
            <Field label="Panels"><MultiSelect options={panels} labelKey="name" idKey="panel_id" selected={form.panel_ids} onChange={(ids) => setForm({ ...form, panel_ids: ids })} testid="package-panels" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="package-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="package-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-package-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
